import { Button } from '@/components/ui/button';
import { DebouncedInput } from '@/components/ui/debounced-input';
import { Tag, Trash2 } from 'lucide-react';

interface ConnectionEditorProps {
    connection: ManuscriptConnection;
    onUpdateLabel: (connId: string, label: string) => void;
    onToggleDirection: (connId: string, currentStyleId: string) => void;
    onDelete: (connId: string) => void;
}

/**
 * ConnectionEditor - Inline editor for a manuscript connection.
 * 
 * Displays:
 * - A toggle to switch between Arrow (directed) and Line (undirected)
 * - A debounced input for the connection label
 * - A delete button
 */
export function ConnectionEditor({
    connection,
    onUpdateLabel,
    onToggleDirection,
    onDelete
}: ConnectionEditorProps) {
    const isArrow = connection.connectionStyleId === 'default-arrow';

    return (
        <div className="space-y-2 p-3 bg-muted/20 rounded-lg border border-brand/10">
            <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest">
                    Connection
                </span>
                <div className="flex items-center gap-1">
                    <Button
                        variant={isArrow ? 'default' : 'outline'}
                        size="sm"
                        className="h-6 px-2 text-[10px]"
                        onClick={() => onToggleDirection(connection.id, connection.connectionStyleId)}
                    >
                        {isArrow ? 'Arrow' : 'Line'}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => onDelete(connection.id)}
                    >
                        <Trash2 className="h-3 w-3" />
                    </Button>
                </div>
            </div>
            <div className="flex gap-2 items-center">
                <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <DebouncedInput
                    placeholder="Label (e.g. parallelism)"
                    value={connection.label || ''}
                    onSave={(val) => onUpdateLabel(connection.id, val)}
                />
            </div>
        </div>
    );
}
